import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, combineLatest, map, takeUntil } from 'rxjs';
import { CategoryService } from 'src/app/services/category.service';
import { DataService } from 'src/app/services/data.service';
import { LocalizationService } from 'src/app/services/localization.service';
import { ProductService } from 'src/app/services/product.service';
import { StoplistService } from 'src/app/services/stoplist.service';
import { hasAllergens } from 'src/app/components/menu-allergy-notice/menu-allergy-notice.component';
import { matchesQuery, queryWords } from 'src/app/utils/menu-search';
import { Category } from 'src/interfaces/category.model';
import { Details } from 'src/interfaces/details.model';
import { Labels } from 'src/interfaces/enums.model';
import { Restaurant } from 'src/interfaces/restaurant.model';
import { LANGUAGE_NAMES } from '../desktop-translations';
import { MenuItem, MenuSection, buildMenu, sectionAnchor } from '../desktop-menu.model';

/** Ключ localStorage: язык, выбранный гостем в подвале */
export const MENU_LANGUAGE_KEY = 'menu_language';

/** Высота закреплённой полосы категорий с запасом — для подсветки вкладки */
const TABS_OFFSET = 100;

/** Пауза в событиях прокрутки, после которой переход по вкладке считается законченным */
const SCROLL_SETTLE = 150;

/**
 * Меню по ссылке заведения (slug или id, без стола) на широком экране —
 * самостоятельная вёрстка для просмотра: без корзины, заказа и вызова официанта
 * (docs/design/README.md приложения Edim, раздел 16; ROADMAP 3b)
 */
@Component({
    selector: 'app-desktop-menu',
    templateUrl: './desktop-menu.component.html',
    styleUrls: ['./desktop-menu.component.css'],
    standalone: false
})
export class DesktopMenuComponent implements OnInit, OnDestroy {
  @ViewChild('tabStrip') tabStrip?: ElementRef<HTMLElement>;

  readonly Labels = Labels;
  readonly anchor = sectionAnchor;

  items: MenuItem[] = [];
  sections: MenuSection[] = [];
  premiere: MenuSection | null = null;
  categories: Category[] = [];

  /** Активная вкладка — id категории */
  active = '';
  query = '';
  results: MenuItem[] = [];

  /** Позиция в открытом диалоге */
  opened: MenuItem | null = null;

  contactsOpen = false;
  /** Что скопировано последним: 'menu' | 'item' */
  copied: 'menu' | 'item' | null = null;

  allergyNotice$: Observable<boolean>;

  private pendingItem: string | null;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;
  private frame = 0;
  /** Идёт плавная прокрутка к разделу после нажатия на вкладку */
  private jumping = false;
  private jumpTimer: ReturnType<typeof setTimeout> | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    public dataService: DataService,
    private categoryService: CategoryService,
    private productService: ProductService,
    private stoplistService: StoplistService,
    private localization: LocalizationService,
    route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {
    this.allergyNotice$ = combineLatest([productService.foods$, productService.drinks$]).pipe(
      map(([foods, drinks]) => [...foods, ...drinks].some(hasAllergens)),
    );
    this.pendingItem = route.snapshot.queryParamMap.get('item');
  }

  get restaurant(): Restaurant {
    return this.dataService.restaurant!;
  }

  get language(): string {
    return this.dataService.language;
  }

  /** Язык заведения и переводы меню — в переключателе подвала */
  get languages(): string[] {
    const locale = this.dataService.locale;
    const all = [locale?.code ?? '', ...(locale?.languages ?? [])];
    return [...new Set(all)].filter((lang) => LANGUAGE_NAMES[lang]);
  }

  /**
   * Контакты — как в мобильном подвале: не у демо и если реквизиты заполнены.
   * У промо-заведения реквизиты текущего аккаунта, а не будущего владельца
   */
  get details(): Details | null {
    const details = this.restaurant.details;
    if (this.restaurant.demo || this.restaurant.promo === true || !details) return null;
    return Object.values(details).some((value) => !!value) ? details : null;
  }

  get tabs(): MenuSection[] {
    return this.premiere ? [this.premiere, ...this.sections] : this.sections;
  }

  ngOnInit(): void {
    combineLatest([
      this.categoryService.categories$,
      this.productService.foods$,
      this.productService.drinks$,
      this.stoplistService.foods$,
      this.stoplistService.drinks$,
    ]).pipe(takeUntil(this.destroy$)).subscribe(([categories, foods, drinks, stoplistFoods, stoplistDrinks]) => {
      const menu = buildMenu(categories, foods, drinks, stoplistFoods, stoplistDrinks);
      this.categories = categories;
      this.items = menu.items;
      this.sections = menu.sections;
      this.premiere = menu.premiere;
      // Блюда и напитки приходят в разном порядке: первая порция может быть без
      // первых разделов, поэтому вкладка пересчитывается после каждого обновления
      if (!this.tabs.some((tab) => tab.category.id === this.active)) this.active = this.tabs[0]?.category.id ?? '';
      this.onScroll();
      this.search();
      this.openPending();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    cancelAnimationFrame(this.frame);
    if (this.jumpTimer) clearTimeout(this.jumpTimer);
    this.lockScroll(false);
  }

  // Вкладки

  /**
   * Нажатая вкладка подсвечивается сразу и не меняется, пока страница плавно едет
   * к разделу: иначе по пути подсвечивались бы все промежуточные разделы
   */
  scrollTo(section: MenuSection): void {
    this.clearSearch();
    this.active = section.category.id;
    this.jumping = true;
    this.settleJump();
    setTimeout(() => {
      this.revealTab();
      document.getElementById(sectionAnchor(section.category))?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.jumping) {
      this.settleJump();
      return;
    }
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateActive());
  }

  /** Переход закончен, когда события прокрутки стихли */
  private settleJump(): void {
    if (this.jumpTimer) clearTimeout(this.jumpTimer);
    this.jumpTimer = setTimeout(() => (this.jumping = false), SCROLL_SETTLE);
  }

  /**
   * Подсветка вкладки раздела, который сейчас под полосой категорий. В конце
   * страницы — последний раздел: короткий последний раздел до полосы не доезжает
   */
  private updateActive(): void {
    if (this.query || this.jumping) return;
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    let current = this.tabs[0]?.category.id ?? '';
    for (const tab of this.tabs) {
      const el = document.getElementById(sectionAnchor(tab.category));
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= TABS_OFFSET || (atBottom && window.scrollY > 0 && top < window.innerHeight)) current = tab.category.id;
    }
    if (current !== this.active) {
      this.active = current;
      this.cdr.detectChanges();
      this.revealTab();
    }
  }

  /**
   * Активная вкладка — в видимой части полосы. Положение — относительно самой полосы:
   * offsetLeft считается от закреплённой .tabs во всю ширину и включает поля .wrap
   */
  private revealTab(): void {
    const strip = this.tabStrip?.nativeElement;
    const tab = strip?.querySelector<HTMLElement>('.tab.on');
    if (!strip || !tab) return;
    const bounds = strip.getBoundingClientRect();
    const rect = tab.getBoundingClientRect();
    if (rect.left < bounds.left || rect.right > bounds.right) {
      strip.scrollBy({ left: rect.left - bounds.left - 16, behavior: 'smooth' });
    }
  }

  // Поиск — только по этому меню, стоп-лист не ищется

  /**
   * Если полоса категорий уже закреплена, первая буква поднимает результаты под неё:
   * полоса с полем поиска остаётся на месте. Если страница наверху — прокрутки нет
   */
  onQuery(value: string): void {
    const wasEmpty = !this.query;
    this.query = value;
    this.search();
    const top = this.searchTop();
    if (wasEmpty && value && window.scrollY > top) window.scrollTo({ top });
  }

  clearSearch(): void {
    this.query = '';
    this.results = [];
  }

  private search(): void {
    const words = queryWords(this.query);
    this.results = words.length
      ? this.sections.flatMap((s) => s.items).filter((i) => !i.stoplist && matchesQuery(i.item, words, this.localization))
      : [];
  }

  /** Результаты — сразу под полосой категорий (метка перед закреплённой полосой) */
  private searchTop(): number {
    const anchor = document.querySelector('.tabs-anchor');
    return anchor ? anchor.getBoundingClientRect().top + window.scrollY : 0;
  }

  // Диалог позиции

  /** Рекомендация открывается в том же окне */
  open(entry: MenuItem): void {
    if (entry.stoplist) return;
    this.opened = entry;
    this.lockScroll(true);
  }

  closeDialog(): void {
    this.opened = null;
    this.lockScroll(false);
  }

  /**
   * Страница под диалогом не прокручивается. Полоса прокрутки при этом пропадает —
   * её ширина возвращается отступом, чтобы контент не становился шире
   */
  private lockScroll(lock: boolean): void {
    const body = document.body.style;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (lock && body.overflow !== 'hidden') {
      body.paddingRight = scrollbar > 0 ? `${scrollbar}px` : '';
      body.overflow = 'hidden';
    } else if (!lock) {
      body.paddingRight = '';
      body.overflow = '';
    }
  }

  /** `?item=<id>` — меню открывается сразу с этой позицией */
  private openPending(): void {
    if (!this.pendingItem) return;
    const entry = this.items.find((i) => i.item.id === this.pendingItem && !i.stoplist);
    if (!entry) return;
    this.pendingItem = null;
    this.open(entry);
  }

  // Ссылки и контакты

  /** Короткая ссылка заведения; без slug — адрес по id */
  private get menuUrl(): string {
    return `${location.origin}/${this.restaurant.slug || this.restaurant.id}`;
  }

  copyMenuLink(): void {
    this.copy(this.menuUrl, 'menu');
  }

  copyItemLink(): void {
    if (this.opened) this.copy(`${this.menuUrl}?item=${this.opened.item.id}`, 'item');
  }

  private copy(url: string, what: 'menu' | 'item'): void {
    void navigator.clipboard?.writeText(url).then(() => {
      this.copied = what;
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => (this.copied = null), 2000);
    });
  }

  @HostListener('document:keydown.escape')
  closeContacts(): void {
    this.contactsOpen = false;
  }

  // Язык

  /** Перевод меню на выбранный язык: переводы — чистые пайпы, поэтому страница перерисовывается */
  setLanguage(lang: string): void {
    try {
      localStorage.setItem(MENU_LANGUAGE_KEY, lang);
    } catch {
      // без localStorage выбор действует до перезагрузки
    }
    document.documentElement.lang = lang;
    this.dataService.language = lang;
    this.search();
  }
}
